import React, { useMemo } from 'react';

import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';

import { twMerge } from 'tailwind-merge';

import Popover from 'components/pop-over/popover';
import Typography from 'components/typography/typography';

import Icon from 'components/icon/icon';

import TimeUnitInput from './time-unit-input';

interface TimeInputProps {
  className?: string;
  seperator?: string;
  disabled?: boolean;
  defaultValue?: Dayjs;
  hasError?: boolean;
  name: string;
  value?: Dayjs;
  onChange?: (target: { value?: Dayjs; name: string }) => void;
  minuteStep?: number;
}

const TimeInput: React.FC<TimeInputProps> = (props) => {
  const {
    className,
    seperator,
    disabled,
    hasError,
    value,
    name,
    defaultValue,
    onChange,
    minuteStep = 1,
  } = props;

  const onTimeChange = (target: { name: 'hour' | 'minute'; value?: Dayjs }) => {
    const timeFormat = 'HH:mm';
    let newTime: Dayjs;
    if (target.name === 'hour') {
      newTime = dayjs(
        `${target.value?.format('HH')}:${
          value?.format('mm') ?? dayjs().format('mm')
        }`,
        timeFormat,
      );
    } else {
      newTime = dayjs(
        `${value?.format('HH') ?? dayjs().format('HH')}:${target.value?.format(
          'mm',
        )}`,
        timeFormat,
      );
    }

    onChange?.({ name, value: newTime });
  };

  const onHourChange = (hours: number) => {
    const currentHour = value ?? dayjs('00', 'HH');
    const nextHour = currentHour.add(hours, 'hour');
    onChange?.({ name, value: nextHour });
  };

  const onMinuteChange = (minutes: number) => {
    const currentMinute = value ?? dayjs('00', 'm');
    const nextMinute = currentMinute.add(minutes, 'minute');
    onChange?.({ name, value: nextMinute });
  };

  const displayedTime = useMemo(() => {
    if (disabled || !value) return '';

    return `${value.format('HH:mm')}`;
  }, [disabled, value]);

  const inputClassNames = twMerge(
    'pl-3 ',
    hasError && 'border-2 border-tomato-red',
    'border-2 border-gray100 px-3 py-2 outline-TimeInputnone rounded focus:shadow-20',
    'transition-shadow ease-in-out hover:transition-shadow hover:ease-in-out',
    disabled && 'bg-gray100',
  );

  return (
    <div className="flex gap-x-1 ">
      <Popover
        button={
          <button
            type="button"
            className={twMerge('rounded', disabled && 'bg-gray100')}
            disabled={disabled}
          >
            <input
              type="text"
              value={displayedTime}
              maxLength={5}
              className={inputClassNames}
              onChange={() => {}}
              disabled={disabled}
            />
          </button>
        }
        position="bottom"
        align="start"
        className={className}
        hideOnClick={false}
      >
        <div className="flex items-center min-w-max py-1 rounded-md shadow-4">
          <TimeUnitInput
            value={value?.format('HH')}
            onChange={onTimeChange}
            onUpClicked={() => onHourChange(+1)}
            onDownClicked={() => onHourChange(-1)}
            name="hour"
            max={24}
          />
          <Typography as="p" variant="assistive">
            {seperator}
          </Typography>
          <TimeUnitInput
            value={value?.format('mm')}
            onChange={onTimeChange}
            onUpClicked={() => onMinuteChange(+minuteStep)}
            onDownClicked={() => onMinuteChange(-minuteStep)}
            name="minute"
            max={60}
          />
        </div>
      </Popover>
      <button
        type="button"
        onClick={() => onChange?.({ name, value: defaultValue || undefined })}
        className={twMerge(
          'px-3 py-2 transition-shadow ease-in-out',
          'hover:transition-shadow hover:ease-in-out',
          'border-2 border-gray100 rounded',
          disabled && 'bg-gray100',
        )}
        disabled={disabled}
      >
        <Icon icon="close" />
      </button>
    </div>
  );
};

export default TimeInput;
